import {
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent
} from '../services/calendarApi';

const formatDateOnly = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDateTimeLocal = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}`;
};

const hasTimePart = (value) => typeof value === 'string' && value.includes('T');

const parseDateTimeParts = (value, fallbackTime) => {
  if (!value) return { date: '', time: fallbackTime };
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2}))?/);
    if (m) {
      return {
        date: m[1],
        time: m[2] || fallbackTime,
      };
    }
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: '', time: fallbackTime };
  return {
    date: formatDateOnly(date),
    time: `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`,
  };
};

const combineDateTime = (date, time, fallbackTime) => {
  if (!date) return '';
  const safeTime = time || fallbackTime;
  return `${date}T${safeTime}`;
};

const normalizeDateTimeValue = (value, fallbackTime) => {
  if (!value) return '';
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return value;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T${fallbackTime}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return formatDateTimeLocal(date);
};

const parseDateOnlyLocal = (value) => {
  const m = typeof value === 'string' ? value.match(/^(\d{4})-(\d{2})-(\d{2})$/) : null;
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
};

const applyCalendarDelta = (baseDate, delta) => {
  const d = new Date(baseDate.getTime());
  if (!delta) return d;

  const years = Number(delta.years || 0);
  const months = Number(delta.months || 0);
  const days = Number(delta.days || 0);
  const milliseconds = Number(delta.milliseconds ?? delta.ms ?? 0);

  if (years) d.setFullYear(d.getFullYear() + years);
  if (months) d.setMonth(d.getMonth() + months);
  if (days) d.setDate(d.getDate() + days);
  if (milliseconds) d.setTime(d.getTime() + milliseconds);

  return d;
};

/**
 * 日付クリック処理を生成
 */
export const createDateClickHandler = (calendarState) => {
  return (arg) => {
    calendarState.setDefaultEvent(arg.dateStr);
    calendarState.setOpen(true);
  };
};

/**
 * イベントクリック処理を生成
 *
 * 以前は loadCalendarEvents() を毎クリック呼び出して全件を再取得していたが、
 * すでに state（calendarEvents.events）に同じデータがあるためローカル参照に変更し、
 * モーダルが即時開くようにする。
 */
export const createEventClickHandler = (calendarState, calendarEvents) => {
  return (info) => {
    try {
      const clickedEvent = calendarEvents?.events?.find(event => String(event.id) === String(info.event.id));
      if (!clickedEvent) return;

      const parsedStart = parseDateTimeParts(clickedEvent.start, '00:00');
      const parsedEnd = parseDateTimeParts(clickedEvent.end || clickedEvent.start, '00:00');
      calendarState.setTempEvent({
        id: clickedEvent.id,
        title: clickedEvent.title || '',
        notes: clickedEvent.notes || '',
        start: normalizeDateTimeValue(clickedEvent.start, '00:00'),
        end: normalizeDateTimeValue(clickedEvent.end || clickedEvent.start, '00:00'),
        startTime: parsedStart.time,
        endTime: parsedEnd.time,
        category: clickedEvent.category || '社会',
        person: clickedEvent.person || '',
        area: clickedEvent.area || '本社'
      });
      calendarState.setOpen(true);
    } catch (error) {
      console.error('スケジュール情報の取得エラー:', error);
    }
  };
};

/**
 * ドラッグ&ドロップ処理を生成
 *
 * 以前はドラッグごとに loadCalendarEvents() で全件再取得していたが、
 * すでに state にある calendarEvents.events から参照する形に変更し、
 * 体感速度を改善する。
 */
export const createEventDropHandler = (calendarEvents) => {
  return async (info) => {
    try {
      const loadedEvents = calendarEvents.events;
      const droppedEvent = loadedEvents.find(event => String(event.id) === String(info.event.id));

      if (droppedEvent) {
        const originalHasTime = hasTimePart(droppedEvent.start) || hasTimePart(droppedEvent.end);
        let updatedEvent;

        if (originalHasTime) {
          const normalizedStart = normalizeDateTimeValue(droppedEvent.start, '00:00');
          const normalizedEnd = normalizeDateTimeValue(droppedEvent.end || droppedEvent.start, '00:00');
          const originalStartDate = normalizedStart ? new Date(normalizedStart) : null;
          const originalEndDate = normalizedEnd ? new Date(normalizedEnd) : originalStartDate;
          if (!originalStartDate || Number.isNaN(originalStartDate.getTime())) return;

          const shiftedStart = applyCalendarDelta(originalStartDate, info.delta);
          const shiftedEnd = originalEndDate ? applyCalendarDelta(originalEndDate, info.delta) : shiftedStart;

          updatedEvent = {
            ...droppedEvent,
            start: formatDateTimeLocal(shiftedStart),
            end: formatDateTimeLocal(shiftedEnd),
          };
        } else {
          const originalStartDate = parseDateOnlyLocal(droppedEvent.start);
          const originalEndDate = parseDateOnlyLocal(droppedEvent.end || droppedEvent.start) || originalStartDate;
          if (!originalStartDate || Number.isNaN(originalStartDate.getTime())) return;

          const shiftedStart = applyCalendarDelta(originalStartDate, info.delta);
          const shiftedEnd = originalEndDate ? applyCalendarDelta(originalEndDate, info.delta) : shiftedStart;

          updatedEvent = {
            ...droppedEvent,
            start: formatDateOnly(shiftedStart),
            end: formatDateOnly(shiftedEnd),
          };
        }

        const updatedEvents = loadedEvents.map(event =>
          event.id === updatedEvent.id ? updatedEvent : event
        );
        calendarEvents.setEvents(updatedEvents);

        await updateCalendarEvent(updatedEvent.id, updatedEvent);
      }
    } catch (error) {
      console.error('スケジュール移動エラー:', error);
    }
  };
};

/**
 * 削除処理を生成
 */
export const createDeleteHandler = (calendarState, calendarEvents) => {
  return async () => {
    if (calendarState.isSaving || !calendarState.tempEvent.id) return;
    
    if (!window.confirm('このスケジュールを削除してもよろしいですか？')) return;
    
    calendarState.setIsSaving(true);
    
    try {
      const result = await deleteCalendarEvent(String(calendarState.tempEvent.id));
      if (result === false) {
        throw new Error('削除処理に失敗しました');
      }
      
      calendarEvents.setEvents(calendarEvents.events.filter(event => 
        String(event.id) !== String(calendarState.tempEvent.id)
      ));
      calendarState.setOpen(false);
    } catch (error) {
      alert('削除処理に失敗しました');
      console.error('削除エラー:', error);
    } finally {
      calendarState.setIsSaving(false);
    }
  };
};

/**
 * 保存処理を生成
 */
export const createSaveHandler = (calendarState, calendarEvents, currentUser) => {
  return async () => {
    if (calendarState.isSaving) return;
    calendarState.setIsSaving(true);

    try {
      const { tempEvent } = calendarState;
      const startDatePart = parseDateTimeParts(tempEvent.start, '00:00').date;
      const endDatePart = parseDateTimeParts(tempEvent.end, '00:00').date;
      const normalizedStart = normalizeDateTimeValue(
        combineDateTime(startDatePart, tempEvent.startTime, '00:00'),
        '00:00'
      );
      const normalizedEnd = normalizeDateTimeValue(
        combineDateTime(endDatePart, tempEvent.endTime, '00:00'),
        '00:00'
      );
      
      // 入力値検証
      if (!normalizedStart || !normalizedEnd) {
        alert('開始日時と終了日時は必須です');
        return;
      }

      if (new Date(normalizedStart) > new Date(normalizedEnd)) {
        alert("終了日は開始日より後の日付を選択してください。");
        return;
      }

      if (tempEvent.id) {
        // 更新処理
        const { startTime, endTime, ...eventWithoutUiTimes } = tempEvent;
        const updatedEvent = {
          ...eventWithoutUiTimes,
          start: normalizedStart,
          end: normalizedEnd,
          date: normalizedStart,
          title: tempEvent.title || '',
          notes: tempEvent.notes || '',
          person: tempEvent.person || '',
          category: tempEvent.category || '社会',
          area: tempEvent.area || '本社',
          updatedBy: currentUser?.userId,
          updatedByUsername: currentUser?.username
        };
        
        await updateCalendarEvent(tempEvent.id, updatedEvent);
        calendarEvents.setEvents(calendarEvents.events.map(event =>
          event.id === tempEvent.id ? updatedEvent : event
        ));
        
      } else {
        // 新規作成処理
        const { startTime, endTime, ...eventWithoutUiTimes } = tempEvent;
        const newEvent = {
          ...eventWithoutUiTimes,
          start: normalizedStart,
          end: normalizedEnd,
          date: normalizedStart,
          title: tempEvent.title || '',
          notes: tempEvent.notes || '',
          person: tempEvent.person || '',
          category: tempEvent.category || '社会',
          area: tempEvent.area || '本社',
          createdBy: currentUser?.userId,
          createdByUsername: currentUser?.username
        };
        
        // 重複チェック
        const isDuplicate = calendarEvents.events.some(event =>
          event.title === newEvent.title &&
          event.notes === newEvent.notes &&
          event.person === newEvent.person &&
          event.start === newEvent.start &&
          event.end === newEvent.end &&
          event.category === newEvent.category &&
          event.area === newEvent.area
        );
        
        if (isDuplicate) {
          alert('同じ内容のスケジュールが既に登録されています');
          return;
        }

        const savedEvent = await createCalendarEvent(newEvent);
        calendarEvents.setEvents([...calendarEvents.events, savedEvent]);
      }
      
      calendarState.setOpen(false);
      
    } catch (error) {
      alert('保存に失敗しました');
      console.error('保存エラー:', error);
    } finally {
      calendarState.setIsSaving(false);
    }
  };
};